import { Col, Form, FormInstance, Row } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Progress } from "antd";
import { isMobile } from "react-device-detect";
import { RightOutlined, LoadingOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useState } from "react";
import {
  StepContainer,
  FirstStep,
  SecondStep,
  FourthStep,
  FifthStep,
  ThirdStep,
  SixthStep,
} from "./steps";
import { createUpcomingRelease } from "shared/service/StrmService";
import { UpcomingRelease } from "shared/interfaces/UpcomingRelease";
import { useSelector } from "react-redux";
import "./NewPitching.scss";

interface Step {
  step: number;
  title: string | null;
  content: (visible: boolean) => React.ReactNode;
}

const secondStepValidation = [
  "type",
  "name",
  "genres",
  "genresOthersName",
  "releaseOn",
  "labelName",
  "focusTrack",
];
const thirdStepValidation = [
  "moods",
  "musicStyles",
  "instrumentsUsed",
  "isCover",
  "recorded",
  "isRemix",
  "labelName",
  "isInstrumental",
  "language",
  "isrc",
  "upc",
  "musicalCultures",
];
const fourthStepValidation = [
  "photosURL",
  "planningFilesURL",
  "songUrl",
  "releaseMaterials",
];
const fifthStepValidation = ["aboutCreation"];
const sixthStepValidation = ["pitching"];

const isStepValidToContinue = async (
  step: number,
  form: FormInstance<UpcomingRelease>
): Promise<boolean> => {
  const validationsByStep = new Map();
  validationsByStep.set(1, []);
  validationsByStep.set(2, secondStepValidation);
  validationsByStep.set(3, thirdStepValidation);
  validationsByStep.set(4, fourthStepValidation);
  validationsByStep.set(5, fifthStepValidation);
  validationsByStep.set(6, sixthStepValidation);

  try {
    await form.validateFields(validationsByStep.get(step));
    return true;
  } catch (error) {
    return false;
  }
};

const stepToString = (step: number) => {
  const stepNames = ["first", "second", "third", "fourth", "fifth", "sixth"];
  return stepNames[step - 1];
};

const NewPitching: React.FC<any> = (props) => {
  const { loggedUser } = useSelector((state: any) => state.users);
  const [step, setStep] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const [form] = Form.useForm<UpcomingRelease>();
  const history = useHistory();
  const artistMustAnswerQuiz = !loggedUser?.isReleasePlanningEnable;

  const steps: Step[] = [
    {
      step: 1,
      title: t("pages.Pitching.steps.first.title"),
      content: (visible) => <FirstStep visible={visible} />,
    },
    {
      step: 2,
      title: t("pages.Pitching.steps.second.title"),
      content: (visible) => <SecondStep visible={visible} />,
    },
    {
      step: 3,
      title: t("pages.Pitching.steps.fourth.title"),
      content: (visible) => <ThirdStep visible={visible} />,
    },
    {
      step: 4,
      title: t("pages.Pitching.steps.fourth.title"),
      content: (visible) => <FourthStep visible={visible} />,
    },
    {
      step: 5,
      title: t("pages.Pitching.steps.fifth.title"),
      content: (visible) => <FifthStep visible={visible} />,
    },
    {
      step: 6,
      title: null,
      content: (visible) => <SixthStep visible={visible} />,
    },
  ];

  const getTitleProgressBar = () =>
    t(`pages.Pitching.steps.${stepToString(step)}.progressBar`);

  const submitForm = async () => {
    try {
      const formValues = form.getFieldsValue();
      setLoading(true);
      await createUpcomingRelease(formValues);
      toast.success<string>(t("pages.Pitching.salvoSucesso"));
      setLoading(false);
      history.push("/pitching");
    } catch {
      setLoading(false);
      toast.error<string>(t("pages.Pitching.erroAoSalvar"));
    }
  };

  const isLastStep = () => step === steps.length;

  const nextStep = async () => {
    if (await isStepValidToContinue(step, form)) {
      if (isLastStep()) {
        await submitForm();
      } else {
        setStep(step + 1);
      }
    }
  };

  const renderButtonNextContent = () => {
    if (loading) {
      return (
        <>
          <LoadingOutlined />
          {t("pages.Pitching.enviar")}
        </>
      );
    }

    return (
      <>
        {step < steps.length
          ? t("pages.Pitching.avancar")
          : t("pages.Pitching.enviar")}
        <RightOutlined />
      </>
    );
  };

  return (
    <div className={`container-pitching ${isMobile && "mobile"}`}>
      <div className={`bg ${step === 1 ? "bg-first-step" : "bg-next-steps"}`} />
      <div className="content">
        <Row justify="space-between" className="wrapper-progress-bar">
          <div className="text">{getTitleProgressBar()}</div>
          <div className="text">
            <Trans
              i18nKey={t("pages.Pitching.steps.progress", {
                currentStep: step,
                totalSteps: steps.length,
              })}
            />
          </div>
          <Progress
            percent={(100 / steps.length) * step}
            showInfo={false}
            strokeColor="#CECDFF"
          />
        </Row>
        <Form layout="vertical" form={form}>
          {steps.map((item) => (
            <div
              className={`steps-content`}
              style={{ display: item.step === step ? "block" : "none" }}
              key={item.step}
            >
              <StepContainer
                title={item.title}
                StepContent={item.content}
                visible={item.step === step}
              />
            </div>
          ))}
        </Form>

        <Row className="btn-move-steps">
          <Col xs={24}>
            <button
              className="botao-roxo"
              style={{ width: "100%" }}
              onClick={async () => {
                await nextStep();
              }}
              disabled={loading || artistMustAnswerQuiz}
            >
              {renderButtonNextContent()}
            </button>
          </Col>
          {step > 1 && (
            <Col xs={24}>
              <button
                className="botao-branco-vazado"
                style={{ width: "100%" }}
                onClick={() => setStep(step - 1)}
                disabled={loading}
              >
                {t("pages.Pitching.voltar")}
              </button>
            </Col>
          )}
          {artistMustAnswerQuiz && (
            <Col xs={24}>
              <p className="next-step-blocked-reason">
                {t("pages.Pitching.preencherQuiz")}
              </p>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

export default NewPitching;
